/**
 * Sticky amber banner shown at the very top of AdminLayout while a "View as
 * user" session is active. Identifies the active target and the original
 * super-admin, and exposes an "Exit" button that restores the session.
 *
 * Renders nothing when no impersonation is in flight. The amber outline on
 * <body> (set by the same effect) makes the state hard to forget.
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, LogOut, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useSession, useImpersonator } from '@/hooks/useSession';
import { api, ApiError } from '@/lib/api';

const TTL_MS = 60 * 60 * 1000;

function formatElapsed(startedAt: number | null | undefined): string {
  if (!startedAt) return '';
  const ms = Date.now() - startedAt;
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'just now';
  if (min === 1) return '1 min ago';
  return `${min} min ago`;
}

function formatRemaining(startedAt: number | null | undefined): string {
  if (!startedAt) return '';
  const ms = TTL_MS - (Date.now() - startedAt);
  const min = Math.max(0, Math.floor(ms / 60000));
  return `${min} min left`;
}

export function ImpersonationBanner() {
  const { user } = useSession();
  const impersonator = useImpersonator();
  const [exiting, setExiting] = useState(false);
  const [, forceTick] = useState(0);

  const startedAt = user?.impersonationStartedAt ?? null;
  const isActive = !!impersonator;

  // Refresh the elapsed/remaining text every 30s while active.
  useEffect(() => {
    if (!isActive) return;
    const t = setInterval(() => forceTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [isActive]);

  // Visual: 4px amber ring around the viewport so the impersonation state is
  // unmistakable even when the banner scrolls out of view.
  useEffect(() => {
    if (!isActive) return;
    const prev = document.body.style.boxShadow;
    document.body.style.boxShadow = 'inset 0 0 0 4px rgb(245 158 11)';
    return () => { document.body.style.boxShadow = prev; };
  }, [isActive]);

  if (!isActive) return null;

  const targetName = [user?.firstname, user?.lastname].filter(Boolean).join(' ') || user?.email || 'user';
  const impersonatorName = [impersonator.firstname, impersonator.lastname].filter(Boolean).join(' ')
    || impersonator.email;

  async function handleExit() {
    setExiting(true);
    try {
      await api.post('/v4/auth/impersonate/exit', {});
      toast.success('Impersonation ended');
      window.location.assign('/');
    } catch (err) {
      const message = err instanceof ApiError
        ? (typeof err.data === 'object' && err.data && 'error' in err.data
          ? String((err.data as { error: unknown }).error)
          : err.message)
        : 'Failed to end impersonation';
      toast.error(message);
      setExiting(false);
    }
  }

  return (
    <div className="sticky top-0 z-50 bg-amber-500 text-white shadow-md print:hidden">
      <div className="flex items-center gap-3 px-4 py-2 text-sm">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="font-semibold">Viewing as {targetName}</span>
          <span className="opacity-90 ml-2">
            ({user?.email}) — impersonator: <span className="font-medium">{impersonatorName}</span>
            {startedAt && (
              <> · started {formatElapsed(startedAt)} · {formatRemaining(startedAt)}</>
            )}
          </span>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleExit}
          disabled={exiting}
          className="bg-white text-amber-900 hover:bg-amber-50"
        >
          {exiting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LogOut className="h-4 w-4 mr-2" />}
          Exit impersonation
        </Button>
      </div>
    </div>
  );
}

export default ImpersonationBanner;
